import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';

export interface FieldProps {
  label: string;
  error?: string;
  help?: string;
  children: ReactNode;
}

/**
 * Field wraps a form control with its label, optional help text, and inline
 * error. To make label/control association work for screen readers we
 * generate an id with useId(), inject it into the first valid-element child
 * if it doesn't already have one, and link it via htmlFor on the <label>.
 *
 * Children may be a single control (input/select/textarea) or a small
 * compound (e.g. an input + an adornment button); we only id-link the first
 * one and pass the rest through unchanged.
 */
export function Field({ label, error, help, children }: FieldProps) {
  const generated = useId();
  const childArray = Children.toArray(children);
  const firstElementIndex = childArray.findIndex((c) => isValidElement(c));

  let labelTargetId: string | undefined;
  let enhancedChildren: ReactNode = children;

  if (firstElementIndex !== -1) {
    const first = childArray[firstElementIndex] as ReactElement<{ id?: string }>;
    const existingId = first.props.id;
    labelTargetId = existingId ?? generated;
    if (!existingId) {
      const cloned = cloneElement(first, { id: generated });
      const next = childArray.slice();
      next[firstElementIndex] = cloned;
      enhancedChildren = next;
    }
  }

  return (
    <div className="field">
      <label htmlFor={labelTargetId}>{label}</label>
      {enhancedChildren}
      {help && <div className="help">{help}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}
